/**
 * R-75 — score actual confidence for a single narrative claim from evidence tiers.
 */
import type { DataSource, SiteRagReport } from '../../types/siteRag';
import type { CitationEvidenceCache } from './citationIntegrityVerifier';
import {
  CONFIDENCE_BAND_ORDER,
  CAVEAT_PHRASE_MAP,
  SCORING_WEIGHTS,
  scoreToBand,
  type ConfidenceBand,
} from './confidenceTaxonomy';
import type { Tier1ProviderId } from './providerPrecedenceVerifier';

export type ReferenceTier = 1 | 2 | 3;

export interface ScoredClaimInput {
  /** Claim prose (citations / HTML comments may remain). */
  text: string;
  /** 1-based [Ref N] indices cited in this claim. */
  refIndices: number[];
  sectionId?: string;
  lineNumber?: number;
}

export interface EvidenceBreakdown {
  tier1Score: number;
  tier2Score: number;
  tier3Score: number;
  multiSourceBonus: number;
  brokenCitePenalty: number;
  provenanceGapPenalty: number;
  refTiers: Array<{ index: number; tier: ReferenceTier; source: string }>;
}

export interface ClaimConfidenceScore {
  score: number;
  band: ConfidenceBand;
  breakdown: EvidenceBreakdown;
}

function inferTierFromSource(source: string | undefined): ReferenceTier {
  const s = (source ?? '').toUpperCase();
  const tier1Hit = [
    'NREL', 'USGS', 'EIA', 'FEMA', 'USFWS', 'NRC', 'PEERING', 'HIFLD', 'WRI', 'AIRNOW',
    'EPA', 'NOAA', 'OPEN-METEO', 'GDELT', 'NFHL', 'IPAC', 'ADAMS', 'ARCGIS', 'HIFLD',
  ].some((p) => s.includes(p));
  if (tier1Hit) return 1;
  if (/DAM|CURATED|KNOWLEDGE BASE|REGIONAL BASELINE|GOVERNMENT|STATE AGENCY|COUNTY/i.test(s)) {
    return 2;
  }
  return 3;
}

export function classifyReferenceTier(ref: DataSource | undefined): ReferenceTier {
  if (!ref) return 3;
  const fromField = ref.field?.toLowerCase() ?? '';
  // Research provenance outranks author/title substrings such as Adams or NOAA.
  // A literature citation provides context, not authoritative site evidence.
  if (fromField.startsWith('literature.')) return 3;
  if (/doc|upload|user|developer|teaser|deck|fact sheet/i.test(fromField)) return 3;
  if (/dam|knowledge|baseline|curated/i.test(fromField)) return 2;
  return inferTierFromSource(ref.source);
}

function tierContribution(count: number, perRef: number, cap: number): number {
  if (count <= 0) return 0;
  return Math.min(cap, count * perRef);
}

function hasMultiSourceAgreement(
  refIndices: number[],
  references: DataSource[],
  qaFindings: SiteRagReport['qaFindings'],
): boolean {
  if (refIndices.length < 2) return false;
  const tiers = refIndices.map((i) => classifyReferenceTier(references[i - 1]));
  const strongRefs = tiers.filter((t) => t <= 2).length;
  if (strongRefs >= 2) return true;

  const internal = qaFindings?.internalNumberFindings ?? [];
  const resolved = internal.some(
    (f) => f.resolution && !f.autocorrectBlocked && f.conflictingValues.length <= 1,
  );
  return resolved && refIndices.length >= 2;
}

/*
 * PENALTIES ARE CLAIM-SCOPED (adversarial review, 2026-08-17).
 *
 * Both penalty predicates used to fire REPORT-GLOBALLY: one dangling [Ref]
 * anywhere applied -0.20 to EVERY cited claim (the `|| v.kind === …` arms
 * ignored `v.index`), and any doc-provenance warn anywhere — an image-only PDF
 * — applied -0.15 to every cited claim. Verified by execution: a claim citing
 * two genuinely clean tier-1 refs in a report with one unrelated dangling ref
 * plus one image-only PDF scores 0.65 = moderate, so following the prompt's
 * own HIGH recipe earned a warn stamp on TRUE prose — the expensive
 * false-positive failure, charged to the wrong claim. Worse, the new ceiling
 * block INCREASES declared-HIGH incidence, so the mis-attribution would have
 * scaled with the fix.
 *
 * A penalty now requires the violation to name one of THIS claim's cited
 * indices. A claim citing the dangling index itself is still penalised — the
 * offender keeps paying. Report-level defects keep their own findings and
 * surfaces; they simply stop debiting unrelated claims' confidence.
 */
function brokenCiteForRefs(
  refIndices: number[],
  qaFindings: SiteRagReport['qaFindings'],
): boolean {
  const violations = qaFindings?.citationIntegrityViolations ?? [];
  return refIndices.some((idx) =>
    violations.some((v) => v.severity === 'block' && v.index === idx),
  );
}

function provenanceGapForRefs(
  refIndices: number[],
  qaFindings: SiteRagReport['qaFindings'],
): boolean {
  const citeViolations = qaFindings?.citationIntegrityViolations ?? [];
  return refIndices.some((idx) =>
    citeViolations.some(
      (v) => v.index === idx && v.kind === 'VIOLATION_PROVENANCE_GAP',
    ),
  );
}

/** Detect declared band from caveat phrases (strongest wins). */
export function detectDeclaredBandFromPhrases(text: string): ConfidenceBand | null {
  for (const band of CONFIDENCE_BAND_ORDER) {
    if (CAVEAT_PHRASE_MAP[band].some((re) => re.test(text))) return band;
  }
  return null;
}

export function scoreClaimConfidence(
  claim: ScoredClaimInput,
  report: SiteRagReport,
  _evidence: CitationEvidenceCache,
): ClaimConfidenceScore {
  const references = report.references ?? [];
  const qaFindings = report.qaFindings;
  const refTiers = claim.refIndices.map((index) => {
    const ref = references[index - 1];
    return {
      index,
      tier: classifyReferenceTier(ref),
      source: ref?.source ?? '(unknown)',
    };
  });

  const tier1Count = refTiers.filter((r) => r.tier === 1).length;
  const tier2Count = refTiers.filter((r) => r.tier === 2).length;
  const tier3Count = refTiers.filter((r) => r.tier === 3).length;

  const tier1Score = tierContribution(tier1Count, SCORING_WEIGHTS.tier1, SCORING_WEIGHTS.tier1Cap);
  const tier2Score = tierContribution(tier2Count, SCORING_WEIGHTS.tier2, SCORING_WEIGHTS.tier2Cap);
  const tier3Score = tierContribution(tier3Count, SCORING_WEIGHTS.tier3, SCORING_WEIGHTS.tier3Cap);

  const multiSourceBonus = hasMultiSourceAgreement(claim.refIndices, references, qaFindings)
    ? SCORING_WEIGHTS.multiSourceAgreement
    : 0;
  const brokenCitePenalty = brokenCiteForRefs(claim.refIndices, qaFindings)
    ? SCORING_WEIGHTS.brokenCite
    : 0;
  const provenanceGapPenalty = provenanceGapForRefs(claim.refIndices, qaFindings)
    ? SCORING_WEIGHTS.provenanceGap
    : 0;

  const raw = tier1Score + tier2Score + tier3Score
    + multiSourceBonus + brokenCitePenalty + provenanceGapPenalty;
  const score = Math.max(0, Math.min(1, raw));

  return {
    score,
    band: scoreToBand(score),
    breakdown: {
      tier1Score,
      tier2Score,
      tier3Score,
      multiSourceBonus,
      brokenCitePenalty,
      provenanceGapPenalty,
      refTiers,
    },
  };
}

/** Classify a tier-1 provider id from reference source string (R-78 parity). */
export function inferTier1Provider(source: string | undefined): Tier1ProviderId | null {
  const s = (source ?? '').toUpperCase();
  if (s.includes('HIFLD')) return 'HIFLD';
  if (s.includes('FEMA') || s.includes('NFHL')) return 'FEMA';
  if (s.includes('USFWS') || s.includes('IPAC')) return 'USFWS';
  if (s.includes('NRC') || s.includes('ADAMS')) return 'NRC';
  if (s.includes('PEERING')) return 'PEERINGDB';
  if (s.includes('NREL')) return 'NREL';
  if (s.includes('USGS')) return 'USGS';
  if (s.includes('EIA')) return 'EIA';
  if (s.includes('WRI') || s.includes('AQUEDUCT')) return 'WRI';
  if (s.includes('AIRNOW') || s.includes('EPA AQS')) return 'AIRNOW';
  return null;
}

/**
 * The BEST confidence band a claim citing exactly these reference tiers can
 * earn from `scoreClaimConfidence` — the auditor's own arithmetic, exported so
 * the GENERATOR can be told the grading scale it will be marked against.
 *
 * ── Why this exists (task #31, 2026-08-17) ──────────────────────────────────
 * The caveat-honesty verifier grades each claim by the tiers of the references
 * it cites, but the generator was never told the tiers — the prompt said
 * "never declare high confidence without a tier-1 provider" while the model had
 * no way to know which [Ref N] IS tier 1. It guessed; the auditor did not.
 * Under these weights the guesses were structurally doomed: ONE tier-1 ref
 * scores 0.45 = moderate at best, so "high confidence [Ref 12]" over a single
 * NREL citation is a guaranteed 1-band over-claim, and any number of tier-3
 * developer docs caps at 0.15 = speculative. Live: caveatHonesty stamps
 * survived every prompt-wording fix (11 → 5 → 15 → 13 across four Triple
 * Threat / Redhead regenerations) because wording cannot supply information.
 *
 * Computed via the SAME `tierContribution` + `scoreToBand` the scorer uses —
 * never a hand-written table, so a weight change reprices the prompt
 * automatically. The multi-source bonus is included exactly when the scorer
 * grants it unconditionally (two or more tier-1/tier-2 refs).
 */
export function bandCeilingForRefTiers(tiers: readonly ReferenceTier[]): ConfidenceBand {
  const t1 = tiers.filter((t) => t === 1).length;
  const t2 = tiers.filter((t) => t === 2).length;
  const t3 = tiers.filter((t) => t === 3).length;
  const base =
    tierContribution(t1, SCORING_WEIGHTS.tier1, SCORING_WEIGHTS.tier1Cap)
    + tierContribution(t2, SCORING_WEIGHTS.tier2, SCORING_WEIGHTS.tier2Cap)
    + tierContribution(t3, SCORING_WEIGHTS.tier3, SCORING_WEIGHTS.tier3Cap);
  const multiSource = t1 + t2 >= 2 ? SCORING_WEIGHTS.multiSourceAgreement : 0;
  return scoreToBand(Math.max(0, Math.min(1, base + multiSource)));
}
